/**
 * HHD Recommendations Block Custom Element
 * Fetches product recommendations and initializes a Swiper slider.
 */
if (!customElements.get('hhd-recommendations-block')) {
  class HHDRecommendationsBlock extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.container = this;
      this.url = this.dataset.recommendationsUrl;
      this.moneyFmt = this.dataset.moneyFormat;
      this.pairsCount = this.dataset.pairsCount;
      this.perPairLabel = this.dataset.perPairLabel;

      // Gender filter is opt-in (merchant setting). When enabled the block is
      // fully server-rendered from two collections, so the Recommendations API
      // is skipped entirely.
      if (this.dataset.genderFilterEnabled === 'true') {
        this.initGenderFilter();
        return;
      }

      if (!this.url) return;

      this.fetchRecommendations();
    }

    /* ---------- Gender filter path ---------- */

    initGenderFilter() {
      this.genderProducts = {
        men: this.parseProducts(this.dataset.productsMen),
        women: this.parseProducts(this.dataset.productsWomen)
      };

      this.currentGender = this.dataset.defaultGender === 'women' ? 'women' : 'men';

      // Guard against double-binding if this same instance is reconnected.
      if (!this.genderTabsBound) {
        const tabs = this.querySelectorAll('.hhd-recommendations-block__gender-tab[data-gender]');
        tabs.forEach(tab => {
          tab.addEventListener('click', () => this.selectGender(tab.dataset.gender));
        });
        this.genderTabsBound = true;
      }

      this.renderGender(this.currentGender);
    }

    parseProducts(raw) {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('HHD Recommendations gender products parse error:', e);
        return [];
      }
    }

    selectGender(gender) {
      if (!gender || gender === this.currentGender) return;
      if (!this.genderProducts || !this.genderProducts[gender]) return;

      this.currentGender = gender;
      this.renderGender(gender);
    }

    syncTabs(gender) {
      const tabs = this.querySelectorAll('.hhd-recommendations-block__gender-tab[data-gender]');
      tabs.forEach(tab => {
        const isActive = tab.dataset.gender === gender;
        tab.classList.toggle('hhd-recommendations-block__gender-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    renderGender(gender) {
      this.syncTabs(gender);

      // Destroy before the DOM it owns is replaced, to avoid leaked listeners.
      this.destroySwiper();

      const products = (this.genderProducts && this.genderProducts[gender]) || [];
      this.renderProducts(products);

      const tabsEl = this.querySelector('[data-hhd-gender-tabs]');
      if (tabsEl) tabsEl.style.display = '';

      if (products.length > 0) this.initSwiper();
    }

    destroySwiper() {
      if (this.carousel && this.carousel.slider && typeof this.carousel.slider.destroy === 'function') {
        try {
          this.carousel.slider.destroy(true, true);
        } catch (e) {
          console.error('HHD Recommendations swiper destroy error:', e);
        }
      }
      this.carousel = null;
    }

    fetchRecommendations() {
      fetch(this.url, { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(data => {
          let products = data.products;

          // If no recommendations, try fallback
          if ((!products || products.length === 0) && this.dataset.fallbackProducts) {
            try {
              products = JSON.parse(this.dataset.fallbackProducts);
            } catch (e) {
              console.error('HHD Recommendations fallback parse error:', e);
            }
          }

          if (!products || products.length === 0) return;

          this.renderProducts(products);
          this.initSwiper();
        })
        .catch(e => {
          console.error('HHD Recommendations error:', e);
        });
    }

    formatMoney(cents) {
      if (!this.moneyFmt) return cents;
      let amount = (cents / 100).toFixed(2);
      amount = amount.replace(/\.00$/, '');
      return this.moneyFmt
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
    }

    renderProducts(products) {
      const slidesHtml = products.map(p => {
        const v = p.variants[0];
        const imgSrc = p.featured_image
          ? p.featured_image.replace(/(\.[^.]+)$/, '_400x$1')
          : '';
        const imgTag = imgSrc
          ? `<img class="hhd-slider-card__img" src="${imgSrc}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">`
          : '';

        const onSale = v.compare_at_price && v.compare_at_price > v.price;
        const priceHtml = onSale
          ? `<span class="hhd-slider-card__price--sale">${this.formatMoney(v.price)}</span><span class="hhd-slider-card__price--compare">${this.formatMoney(v.compare_at_price)}</span>`
          : `<span>${this.formatMoney(v.price)}</span>`;

        return `
          <div class="f-column swiper-slide">
            <div class="hhd-slider-card">
              <a href="${p.url}" class="hhd-slider-card__link">
                <div class="hhd-slider-card__image">${imgTag}</div>
                <div class="hhd-slider-card__info">
                  <h3 class="hhd-slider-card__title">${p.title}</h3>
                  <div class="hhd-slider-card__price">${priceHtml}</div>
                </div>
              </a>
              <button type="button" class="hhd-slider-card__add-btn" 
                data-product-id="${p.id}" 
                data-variant-id="${v.id}" 
                data-product-handle="${p.handle}"
                data-pairs-count="${this.pairsCount}"
                data-per-pair-label="${this.perPairLabel}"
              >
                <span class="hhd-slider-card__add-icon">+</span> ADD
              </button>
            </div>
          </div>`;
      }).join('');

      const body = this.querySelector('[data-hhd-body]');
      if (body) {
        body.innerHTML = `
          <div class="hhd-slider__swiper-wrap swiper" style="overflow:hidden;">
            <div class="swiper-wrapper">
              ${slidesHtml}
            </div>
          </div>`;
      }

      // Show header and footer
      const header = this.querySelector('[data-hhd-header]');
      if (header) header.style.display = '';
      const footer = this.querySelector('[data-hhd-footer]');
      if (footer) footer.style.display = '';
    }

    initSwiper() {
      const swiperEl = this.querySelector('.hhd-slider__swiper-wrap');
      const prevBtn = this.querySelector('.swiper-button-prev');
      const nextBtn = this.querySelector('.swiper-button-next');

      if (window.FoxTheme && window.FoxTheme.Carousel && swiperEl) {
        const carousel = new FoxTheme.Carousel(swiperEl, {
          slidesPerView: 2,
          spaceBetween: 12,
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn
          },
          breakpoints: {
            768: {
              slidesPerView: 3,
              spaceBetween: 12
            },
            1280: {
              slidesPerView: 4,
              spaceBetween: 12
            }
          },
          loop: true,
          threshold: 2
        });
        carousel.init();
        this.carousel = carousel;
      }
    }
  }

  customElements.define('hhd-recommendations-block', HHDRecommendationsBlock);
}
